<template>
  <div class="order-list-panel">
    <div class="header-order">
      <button type="button" class="back" @click="$emit('back-home')">
        <img :src="backIcon" alt="返回首页" class="back-icon" />
        <span>返回首页</span>
      </button>

      <h2 class="title">我的订单</h2>
    </div>

    <div class="list">
      <div v-for="order in orders" :key="order.orderId || order.orderNo" class="order-card">
        <div class="header">
          <span class="order-no">订单编号：{{ order.orderNo }}</span>
          <span class="order-status">{{ order.statusText || "--" }}</span>
        </div>

        <div class="meta">
          <p class="meta-item">案由名称：{{ order.caseReason }}</p>
          <p class="meta-item">所属省或直辖市名称：{{ order.location }}</p>
          <p class="meta-item">购买判例时间区间：{{ order.dateRange }}</p>
        </div>

        <div class="summary">
          <p class="summary-item">
            <span>购买案件数：</span>
            <span>{{ order.caseCount }}</span>
          </p>
          <p class="summary-item">
            <span>付费金额：</span>
            <span>{{ order.amount }}</span>
          </p>
        </div>

        <div class="actions">
          <button type="button" class="action" @click="$emit('view-order', order)">
            {{ order.actionText }}
          </button>

          <div v-if="canOfflinePay(order)" class="offline-pay">
            <input :ref="`offline-pay-input-${getOrderKey(order)}`" type="file" class="offline-pay-input"
              accept="image/*" @change="handleOfflinePayFileChange($event, order)" />
            <button type="button" class="action action-upload" @click="triggerOfflinePaySelect(order)">
              {{
                getOfflinePayFileName(order) ? "重新上传凭证" : "上传付款凭证"
              }}
            </button>
            <span v-if="getOfflinePayFileName(order)" class="offline-pay-name">
              {{ getOfflinePayFileName(order) }}
            </span>
            <button type="button" class="action action-offline-pay"
              :disabled="offlinePaySubmittingOrderKey === getOrderKey(order)" @click="submitOfflinePay(order)">
              {{
                offlinePaySubmittingOrderKey === getOrderKey(order)
                  ? "提交中..."
                  : "线下支付"
              }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { isLoggedIn } from "@/util/token-util.js";
import bus from "@/util/bus.js";

const orderStatusList = [0, 1, 2, 3, 4];

function formatDateRange(item) {
  const startDate =
    item.startDate || item.start_date || item.judgmentStartDate || "";
  const endDate = item.endDate || item.end_date || item.judgmentEndDate || "";

  if (startDate || endDate) {
    return [startDate, endDate].filter(Boolean).join("至");
  }

  return item.dateRange || item.date_range || "--";
}

function formatAmount(item) {
  const rawAmount =
    item.totalPrice ||
    item.total_price ||
    item.pay_amount ||
    item.amount ||
    item.price ||
    "";

  if (rawAmount === "" || rawAmount === null || rawAmount === undefined) {
    return "--";
  }

  const amountText = String(rawAmount);
  return amountText.indexOf("¥") === 0 ? amountText : `¥${amountText}`;
}

function extractUploadUrl(uploadRes) {
  const candidates = [
    uploadRes && uploadRes.data,
    uploadRes && uploadRes.url,
    uploadRes && uploadRes.path,
  ];

  for (let i = 0; i < candidates.length; i += 1) {
    const current = candidates[i];

    if (typeof current === "string" && current) {
      return current;
    }

    if (Array.isArray(current)) {
      const firstString = current.find(
        (item) => typeof item === "string" && item,
      );

      if (firstString) {
        return firstString;
      }
    }

    if (current && typeof current === "object") {
      const objectCandidates = [
        current.data,
        current.url,
        current.path,
        current.file,
        current.fileUrl,
        current.file_url,
        current.img,
        current.image,
        current.src,
      ];

      for (let j = 0; j < objectCandidates.length; j += 1) {
        if (typeof objectCandidates[j] === "string" && objectCandidates[j]) {
          return objectCandidates[j];
        }
      }
    }
  }

  return "";
}

function normalizeOrderListItem(item) {
  const orderNo = String(
    item.orderNo || item.order_no || item.order_id || item.id || "",
  );
  const statusRaw = item.status;
  const statusText =
    item.statusText ||
    item.status_name ||
    item.statusName ||
    (statusRaw === null || statusRaw === undefined ? "" : String(statusRaw));

  return {
    id: orderNo,
    orderNo,
    statusText: statusText || "--",
    statusRaw,
    orderId: String(item.id || item.order_id || ""),
    caseReason:
      item.caseCauseName ||
      item.keyword ||
      item.caseReason ||
      item.case_reason ||
      "--",
    location: item.province || item.location || "--",
    dateRange: formatDateRange(item),
    caseCount: String(
      item.quantity || item.caseCount || item.case_count || "0",
    ),
    amount: formatAmount(item),
    actionText: "点击查看您购买的同案由判例简介信息",
    raw: item,
    detailCases: [],
  };
}

function buildOfflinePayPayload(order, imageUrl) {
  const orderId = String(order.orderId || order.id || "").trim();

  return {
    order_id: orderId,
    voucher: imageUrl,
  };
}

export default {
  name: "OrderListPanel",
  props: {
    backIcon: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      orders: [],
      loading: false,
      offlinePayFiles: {},
      offlinePaySubmittingOrderKey: "",
    };
  },
  beforeDestroy() {
    this.releaseOfflinePayFiles();
  },
  created() {
    this.fetchOrders();
  },
  methods: {
    getOrderKey(order) {
      return order.orderId || order.orderNo || order.id || "";
    },
    canOfflinePay(order) {
      const statusText = String(order.statusText || "").trim();
      const statusRaw = String(
        order.statusRaw === null || order.statusRaw === undefined
          ? ""
          : order.statusRaw,
      ).trim();

      return (
        statusRaw === "0" ||
        statusText === "0" ||
        statusText.indexOf("未支付") > -1 ||
        statusText.indexOf("待支付") > -1 ||
        statusText.indexOf("待付款") > -1
      );
    },
    getOfflinePayFileName(order) {
      const orderKey = this.getOrderKey(order);
      return this.offlinePayFiles[orderKey]
        ? this.offlinePayFiles[orderKey].name
        : "";
    },
    triggerOfflinePaySelect(order) {
      const orderKey = this.getOrderKey(order);
      const uploadRef = this.$refs[`offline-pay-input-${orderKey}`];
      const input = Array.isArray(uploadRef) ? uploadRef[0] : uploadRef;

      if (input) {
        input.click();
      }
    },
    handleOfflinePayFileChange(event, order) {
      const file = event.target.files && event.target.files[0];
      const orderKey = this.getOrderKey(order);

      if (!file || !orderKey) {
        return;
      }

      if (String(file.type || "").indexOf("image/") !== 0) {
        this.$message.warning("请上传图片格式的付款凭证");
        event.target.value = "";
        return;
      }

      const currentFileInfo = this.offlinePayFiles[orderKey];

      if (currentFileInfo && currentFileInfo.previewUrl) {
        URL.revokeObjectURL(currentFileInfo.previewUrl);
      }

      this.$set(this.offlinePayFiles, orderKey, {
        file,
        name: file.name,
        previewUrl: URL.createObjectURL(file),
      });
      event.target.value = "";
    },
    releaseOfflinePayFiles() {
      Object.keys(this.offlinePayFiles).forEach((key) => {
        const fileInfo = this.offlinePayFiles[key];

        if (fileInfo && fileInfo.previewUrl) {
          URL.revokeObjectURL(fileInfo.previewUrl);
        }
      });
    },
    clearOfflinePayFile(order) {
      const orderKey = this.getOrderKey(order);
      const fileInfo = this.offlinePayFiles[orderKey];

      if (fileInfo && fileInfo.previewUrl) {
        URL.revokeObjectURL(fileInfo.previewUrl);
      }

      if (orderKey && this.offlinePayFiles[orderKey]) {
        this.$delete(this.offlinePayFiles, orderKey);
      }
    },
    async uploadOfflinePayImage(order) {
      const orderKey = this.getOrderKey(order);
      const fileInfo = this.offlinePayFiles[orderKey];

      if (!fileInfo || !fileInfo.file) {
        return "";
      }

      const formData = new FormData();
      formData.append("fname", fileInfo.file);

      const uploadRes = await this.$uploadApi(formData, "Common/uploadFile");
      const imageUrl = extractUploadUrl(uploadRes);

      return imageUrl;
    },
    async submitOfflinePay(order) {
      const orderKey = this.getOrderKey(order);
      const orderId = order.orderId || order.id || "";

      if (!orderId) {
        this.$message.warning("订单信息异常，请稍后重试");
        return;
      }

      if (!this.offlinePayFiles[orderKey]) {
        this.$message.warning("请先上传付款凭证");
        return;
      }

      this.offlinePaySubmittingOrderKey = orderKey;

      try {
        const imageUrl = await this.uploadOfflinePayImage(order);

        if (!imageUrl) {
          this.$message.error(
            "付款凭证上传成功但未拿到图片地址，请检查上传接口返回",
          );
          return;
        }

        const payload = buildOfflinePayPayload(order, imageUrl);

        const res = await this.$api({
          url: "CaseCause/offlinePay",
          method: "post",
          data: payload,
        });

        if (res.code === 200) {
          this.$message.success(res.msg || "线下支付提交成功");
          this.clearOfflinePayFile(order);
          this.fetchOrders();
          return;
        }

        this.$message.error(res.msg || "线下支付提交失败");
      } catch (error) {
        this.$message.error("线下支付提交失败，请稍后重试");
      } finally {
        this.offlinePaySubmittingOrderKey = "";
      }
    },
    async fetchOrders() {
      if (!isLoggedIn()) {
        this.orders = [];
        bus.$emit("openLoginDialog", "请登录后再查看订单");
        return;
      }

      this.loading = true;

      try {
        const responses = await Promise.all(
          orderStatusList.map((status) =>
            this.$api({
              url: "CaseCause/myOrders",
              method: "get",
              data: {
                status,
                page_num: 50,
              },
            }).catch(() => null),
          ),
        );
        const orderMap = new Map();

        responses.forEach((res) => {
          const list =
            res && res.code === 200 && res.data && Array.isArray(res.data.list)
              ? res.data.list
              : [];

          list.forEach((item) => {
            const order = normalizeOrderListItem(item);

            if (order.orderNo) {
              orderMap.set(order.orderNo, order);
            }
          });
        });

        this.orders = Array.from(orderMap.values());
      } catch (error) {
        this.orders = [];
        this.$message.error("订单列表加载失败，请稍后重试");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.order-list-panel {
  width: 1127px;
}

.header {
  position: relative;
  min-height: 43px;
}

.header-order {
  position: relative;
  min-height: 43px;
}

.back {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  border: 1px solid #d92f24;
  border-radius: 4px;
  padding: 10px 14px 9px 13px;
  background: #ffffff;
  color: #d92f24;
  font-size: 16px;
  line-height: 22px;
  cursor: pointer;
}

.back-icon {
  width: 16px;
  height: 16px;
}

.title {
  position: absolute;
  top: 50%;
  left: 50%;
  margin: 0;
  color: #333333;
  font-size: 16px;
  font-weight: 700;
  line-height: 19px;
  transform: translate(-50%, -50%);
}

.list {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 21px;
}

.order-card {
  border: 1px solid #d4d4d4;
  border-radius: 8px;
  background: #ffffff;
  overflow: hidden;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #d4d4d4;
  border-radius: 8px;
  margin: -1px;
  padding: 14px 25px 13px;
  background: #fff7f7;
}

.order-no {
  color: #d92f24;
  font-size: 18px;
  line-height: 25px;
}

.order-status {
  color: #333333;
  font-size: 16px;
  line-height: 22px;
  white-space: nowrap;
}

.meta {
  display: flex;
  align-items: center;
  gap: 48px;
  padding: 26px 24px 0 25px;
}

.meta-item {
  margin: 0;
  color: #333333;
  font-size: 16px;
  line-height: 22px;
  white-space: nowrap;
}

.summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 210px 0 25px;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  color: #333333;
  font-size: 16px;
  line-height: 22px;
}

.actions {
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 23px 0 26px 25px;
}

.action {
  border: 0;
  border-radius: 4px;
  padding: 9px 23px 8px;
  background: #d92f24;
  color: #ffffff;
  font-size: 16px;
  line-height: 22px;
  cursor: pointer;
}

.action:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.offline-pay {
  display: flex;
  align-items: center;
  gap: 12px;
}

.offline-pay-input {
  display: none;
}

.action-upload {
  background: #ffffff;
  color: #d92f24;
  border: 1px solid #d92f24;
}

.action-offline-pay {
  min-width: 116px;
}

.offline-pay-name {
  max-width: 220px;
  color: #666666;
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
